import React from 'react';
import { ViewDSL, SectionManifest, ValidationResult } from './types';
import { DezzyValidator } from './validator';
import { DezzyCompiler } from './compiler';

// Enhanced interfaces for agent improvements
export interface ConversationTurn {
  id: string;
  userMessage: string;
  agentResponse: string;
  timestamp: Date;
  operationType?: string;
  success: boolean;
  traceId?: string;
  error?: string;
}

export interface OperationRecord {
  operation: AgentOperation;
  response: AgentResponse;
  timestamp: Date;
  executionTime: number;
  success: boolean;
  context: OperationContext;
}

export interface OperationContext {
  projectPath: string;
  gitStatus?: any;
  openFiles: string[];
  userIntent?: UserIntent;
}

export interface UserIntent {
  primary: string;
  confidence: number;
  parameters: Record<string, any>;
}
// Try to load view schema, fallback to basic schema if not available
let viewSchema: any;
try {
  viewSchema = require('@dezzy/schemas/view.schema.json');
} catch {
  // Fallback schema when @dezzy/schemas is not available
  viewSchema = {
    type: 'object',
    properties: {
      meta: { type: 'object' },
      layout: { type: 'object' },
      sections: { type: 'array' }
    },
    required: ['meta', 'layout', 'sections']
  };
}

export interface AgentContext {
  currentProject: {
    sections: SectionManifest[];
    views: ViewDSL[];
    tokens: Record<string, any>;
    projectPath?: string;
  };
  validator: DezzyValidator;
  compiler: DezzyCompiler;
}

export interface AgentOperation {
  type: 'ADD_VARIANT' | 'CREATE_SECTION' | 'UPDATE_VIEW' | 'VALIDATE_DESIGN' 
       | 'PLAN_MIGRATION' | 'EXECUTE_MIGRATION' | 'ROLLBACK_MIGRATION'
       | 'SHOW_STORY' | 'ANALYZE_STORY' | 'SEARCH_COMPONENTS' | 'GENERAL';
  target: string;
  parameters: Record<string, any>;
  context: AgentContext;
}

export interface ActionButton {
  label: string;
  action: 'navigate' | 'search' | 'create' | 'analyze';
  parameters: Record<string, any>;
  variant?: 'primary' | 'secondary' | 'outline';
}

export interface AgentResponse {
  success: boolean;
  data?: any;
  errors?: string[];
  suggestions?: string[];
  actionButtons?: ActionButton[];
  dslChanges?: ViewDSL[];
  validationResults?: ValidationResult[];
  storybookCommand?: StorybookCommand;
  migrationPlan?: {
    steps: Array<{ id: string; description: string; breaking: boolean }>;
    fromVersion: string;
    toVersion: string;
    estimatedTime: number;
    breakingChanges: boolean;
  };
  migrationProgress?: {
    currentStep: number;
    totalSteps: number;
    status: 'planning' | 'executing' | 'completed' | 'failed';
    currentStepDescription?: string;
  };
}

export interface StorybookCommand {
  type: 'navigate' | 'reload' | 'analyze';
  storyId?: string;
  storyTitle?: string;
  params?: Record<string, any>;
}

import { LLMIntentClassifier, ClassificationResult } from './agent/intent-classifier';
import { DezzyTracer } from './tracing/tracer';
import { TraceContext } from './tracing/trace-types';
import { MemoryMonitor } from './tracing/memory-monitor';

export class DezzyAgent {
  private context: AgentContext;
  private promptBuilder: any; // Will be PromptBuilder once integrated
  private conversationHistory: ConversationTurn[] = [];
  private operationHistory: OperationRecord[] = [];
  private lastOperationResult: AgentResponse | null = null;
  private lastTraceId: string | null = null;
  private intentClassifier: LLMIntentClassifier;
  private tracer: DezzyTracer;
  private memoryMonitor: MemoryMonitor;
  private mockStories: Record<string, { id: string; title: string; component: string }> = {
    'button': { 
      id: 'components-button--primary', 
      title: 'Components/Button (Primary)',
      component: 'Button'
    },
    'card': { 
      id: 'components-card--default', 
      title: 'Components/Card (Default)',
      component: 'Card'
    },
    'input': { 
      id: 'components-input--default', 
      title: 'Components/Input (Default)',
      component: 'Input'
    },
    'modal': { 
      id: 'components-modal--default', 
      title: 'Components/Modal (Default)',
      component: 'Modal'
    }
  };
  
  constructor(context: AgentContext) {
    this.context = context;
    this.intentClassifier = new LLMIntentClassifier();
    this.tracer = new DezzyTracer(context.currentProject.projectPath || process.cwd());
    this.memoryMonitor = new MemoryMonitor({
      warningThresholdMB: 150,
      criticalThresholdMB: 300,
      onWarning: (alert) => this.handleMemoryAlert(alert),
      onCritical: (alert) => this.handleMemoryAlert(alert)
    });
    this.memoryMonitor.start();
    this.initializePromptSystem();
  }
  
  private async initializePromptSystem(): Promise<void> {
    // Will be implemented with PromptBuilder integration
    // For now, this sets up the foundation
  }
  
  /**
   * Get the result of the last operation (for accessing storybookCommand)
   */
  getLastOperationResult(): AgentResponse | null {
    return this.lastOperationResult;
  }
  
  /**
   * Get the trace ID of the last conversation (for right-click debugging)
   */
  getLastTraceId(): string | null {
    return this.lastTraceId;
  }

  /**
   * Process natural language messages and convert them to operations
   */
  async handleConversation(message: string, context?: OperationContext): Promise<string> {
    const conversationStartTime = new Date();
    const turnId = this.generateTurnId();
    const conversationId = `conv_${turnId}`;
    
    // Initialize trace context
    const traceContext = this.tracer.startConversationTrace(
      conversationId,
      context?.projectPath || this.context.currentProject.projectPath || process.cwd(),
      '1.0.0' // TODO: Get actual agent version
    );
    
    try {
      // Extract user intent from message using LLM classification with tracing
      const intentStartTime = new Date();
      const intent = await this.extractUserIntent(message);
      const intentTrace = this.tracer.traceIntentClassification(
        traceContext,
        { message, conversationHistory: this.getRecentMessages() },
        intentStartTime,
        {
          intent: intent.primary,
          confidence: intent.confidence,
          extractedEntities: intent.parameters,
          reasoning: intent.parameters.reasoning
        },
        intent.parameters.classifierType || 'fallback'
      );
      
      // Convert intent to operation
      const operation = this.intentToOperation(intent, context);
      
      // Process the operation with tracing
      const operationStartTime = new Date();
      const response = await this.processOperation(operation);
      const operationEndTime = new Date();
      
      const operationTrace = this.tracer.traceOperation(
        traceContext,
        {
          type: operation.type,
          target: operation.target,
          parameters: operation.parameters
        },
        operationStartTime,
        operationEndTime,
        response
      );
      
      // Store the last operation result so we can access storybookCommand
      this.lastOperationResult = response;
      
      // Format response for conversation
      const responseStartTime = new Date();
      const conversationResponse = this.formatConversationResponse(response, intent);
      
      // Complete the conversation trace
      const conversationTrace = this.tracer.completeConversationTrace(
        traceContext,
        message,
        intentTrace,
        operationTrace,
        conversationResponse,
        responseStartTime
      );
      
      // Store trace ID for right-click access
      this.lastTraceId = conversationTrace.traceId;
      
      // Record conversation turn (existing system)
      this.recordConversationTurn(turnId, message, conversationResponse, {
        operationType: operation.type,
        success: response.success,
        executionTime: Date.now() - conversationStartTime.getTime(),
        traceId: conversationTrace.traceId // Add trace ID for correlation
      });
      
      return conversationResponse;
      
    } catch (error) {
      const errorResponse = `I encountered an error processing your request: ${error instanceof Error ? error.message : 'Unknown error'}. Could you please try rephrasing your request?`;
      
      // Log error with tracing context
      console.error('🚨 Conversation Error:', {
        conversationId,
        message,
        error: error instanceof Error ? error.message : 'Unknown error',
        traceContext
      });
      
      this.recordConversationTurn(turnId, message, errorResponse, {
        success: false,
        executionTime: Date.now() - conversationStartTime.getTime(),
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      return errorResponse;
    }
  }

  async processOperation(operation: AgentOperation): Promise<AgentResponse> {
    try {
      switch (operation.type) {
        case 'ADD_VARIANT':
          return this.addVariant(operation);
        case 'CREATE_SECTION':
          return this.createSection(operation);
        case 'UPDATE_VIEW':
          return this.updateView(operation);
        case 'VALIDATE_DESIGN':
          return this.validateDesign(operation);
        case 'PLAN_MIGRATION':
          return this.planMigration(operation);
        case 'EXECUTE_MIGRATION':
          return this.executeMigration(operation);
        case 'ROLLBACK_MIGRATION':
          return this.rollbackMigration(operation);
        case 'SHOW_STORY':
          return this.showStory(operation);
        case 'ANALYZE_STORY':
          return this.analyzeStory(operation);
        case 'SEARCH_COMPONENTS':
          return this.searchComponents(operation);
        case 'GENERAL':
          return this.handleGeneral(operation);
        default:
          return {
            success: false,
            errors: [`Unknown operation type: ${operation.type}`]
          };
      }
    } catch (error) {
      return {
        success: false,
        errors: [error instanceof Error ? error.message : 'Unknown error occurred']
      };
    }
  }

  private async addVariant(operation: AgentOperation): Promise<AgentResponse> {
    const { target, parameters } = operation;
    
    // Run governance check first
    const governanceCheck = await this.validateAgainstGovernance(operation);
    if (!governanceCheck.canProceed) {
      return {
        success: false,
        errors: ['Governance validation failed'],
        suggestions: governanceCheck.recommendations
      };
    }

    // Find existing section
    const existingSection = this.context.currentProject.sections.find(
      section => section.name === target
    );
    
    if (!existingSection) {
      return {
        success: false,
        errors: [`Section "${target}" not found`],
        suggestions: [
          'Available sections: ' + 
          this.context.currentProject.sections.map(s => s.name).join(', '),
          'Use exact section names for better results'
        ]
      };
    }

    // Extract and validate variant details from parameters
    const variantDetails = this.extractVariantDetails(parameters, existingSection);
    if (!variantDetails.valid) {
      return {
        success: false,
        errors: variantDetails.errors,
        suggestions: variantDetails.suggestions
      };
    }

    const newVariant = variantDetails.variant;

    // Check for conflicts with existing variants
    const conflictingVariant = existingSection.variants?.find(
      v => v.name === newVariant.name
    );

    if (conflictingVariant) {
      return {
        success: false,
        errors: [`Variant "${newVariant.name}" already exists in section "${target}"`],
        suggestions: [
          `Consider updating the existing variant instead`,
          `Available variants: ${existingSection.variants?.map(v => v.name).join(', ') || 'none'}`,
          `Try a different name like "${newVariant.name}Alt" or "${newVariant.name}V2"`
        ]
      };
    }

    // Validate new variant against section schema
    const schemaValidation = this.validateVariantSchema(newVariant, existingSection);
    if (!schemaValidation.valid) {
      return {
        success: false,
        errors: schemaValidation.errors || ['Variant validation failed'],
        suggestions: schemaValidation.suggestions || []
      };
    }

    // Create updated section with new variant
    const updatedSection = {
      ...existingSection,
      variants: [...(existingSection.variants || []), newVariant]
    };

    // Generate DSL example for the new variant
    const exampleDSL = this.generateVariantExample(target, newVariant, existingSection);

    return {
      success: true,
      data: {
        section: target,
        variant: newVariant,
        updatedSection,
        exampleDSL,
        storybookStory: {
          name: `${newVariant.name.charAt(0).toUpperCase() + newVariant.name.slice(1)}`,
          args: { ...existingSection.defaultProps, ...newVariant.props }
        }
      },
      suggestions: [
        `✨ Variant "${newVariant.name}" added to section "${target}"`,
        '📖 Update your Storybook stories to showcase the new variant',
        '🧪 Test the variant in different layouts and contexts',
        '♿ Verify accessibility compliance for the new variant'
      ],
      dslChanges: [exampleDSL]
    };
  }

  private extractVariantDetails(parameters: any, existingSection: any): {
    valid: boolean;
    variant?: any;
    errors?: string[];
    suggestions?: string[];
  } {
    const errors: string[] = [];
    const suggestions: string[] = [];

    // Extract variant name
    const variantName = parameters.variantName || parameters.name || 'newVariant';
    if (variantName === 'newVariant') {
      suggestions.push('Consider providing a more descriptive variant name');
    }

    // Extract props with intelligent defaults
    const props = parameters.props || {};
    
    // Suggest common props based on section type
    if (existingSection.name.toLowerCase().includes('button')) {
      if (!props.className && !parameters.style) {
        suggestions.push('Consider specifying className or style for the button variant');
      }
    }

    // Extract tokens and validate format
    const tokens = parameters.tokens || {};
    const invalidTokens = Object.entries(tokens).filter(([key, value]) => 
      typeof value === 'string' && !String(value).startsWith('$')
    );

    if (invalidTokens.length > 0) {
      errors.push(`Tokens should start with $: ${invalidTokens.map(([k]) => k).join(', ')}`);
      suggestions.push('Use design tokens like $colors.primary.500 instead of hardcoded values');
    }

    // Generate description if not provided
    let description = parameters.description;
    if (!description) {
      description = `${variantName} variant of ${existingSection.name}`;
      suggestions.push('Consider adding a description to help other developers understand this variant');
    }

    const variant = {
      name: variantName,
      description,
      props,
      tokens
    };

    return {
      valid: errors.length === 0,
      variant,
      errors: errors.length > 0 ? errors : undefined,
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };
  }

  private validateVariantSchema(variant: any, section: any): {
    valid: boolean;
    errors?: string[];
    suggestions?: string[];
  } {
    const errors: string[] = [];
    const suggestions: string[] = [];

    // Check required variant fields
    if (!variant.name) {
      errors.push('Variant name is required');
    }

    // Validate variant name format
    if (variant.name && !/^[a-zA-Z][a-zA-Z0-9]*$/.test(variant.name)) {
      errors.push('Variant name must start with a letter and contain only alphanumeric characters');
      suggestions.push('Use camelCase for variant names (e.g., "primaryLarge", "secondaryOutlined")');
    }

    // Validate props against section schema
    if (section.schema?.properties && variant.props) {
      for (const [propName, propValue] of Object.entries(variant.props)) {
        const propSchema = section.schema.properties[propName];
        if (propSchema && propSchema.enum) {
          if (!propSchema.enum.includes(propValue)) {
            errors.push(`Invalid value "${propValue}" for prop "${propName}". Allowed: ${propSchema.enum.join(', ')}`);
          }
        }
      }
    }

    // Check for accessibility considerations
    if (section.accessibility?.keyboardSupport && !variant.props?.tabIndex) {
      suggestions.push('Consider keyboard accessibility for interactive elements');
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
      suggestions: suggestions.length > 0 ? suggestions : undefined
    };
  }

  private generateVariantExample(sectionName: string, variant: any, section: any): ViewDSL {
    return {
      meta: {
        name: `${sectionName} ${variant.name} Example`,
        id: `${sectionName.toLowerCase()}-${variant.name.toLowerCase()}-example`,
        version: '1.0.0'
      },
      layout: {
        type: 'stack',
        gap: '$space.4'
      },
      sections: [
        {
          use: sectionName,
          variant: variant.name,
          bindings: {
            ...section.defaultProps,
            ...variant.props,
            // Add sample content based on section type
            ...(sectionName.toLowerCase().includes('button') && { children: `${variant.name} Button` }),
            ...(sectionName.toLowerCase().includes('card') && { 
              title: `${variant.name} Card`,
              children: `This is a ${variant.name} variant of the card component.`
            }),
            ...(sectionName.toLowerCase().includes('input') && { 
              placeholder: `${variant.name} input`,
              label: `${variant.name} Field`
            })
          }
        }
      ]
    };
  }

  private async createSection(operation: AgentOperation): Promise<AgentResponse> {
    // Implement section creation with reuse-first validation
    return {
      success: false,
      errors: ['CREATE_SECTION not yet implemented'],
      suggestions: [
        'Consider adding a variant to an existing section instead',
        'Review the design system for reusable patterns'
      ]
    };
  }

  private async updateView(operation: AgentOperation): Promise<AgentResponse> {
    // Implement DSL view updates
    return {
      success: false,
      errors: ['UPDATE_VIEW not yet implemented']
    };
  }

  private async validateDesign(operation: AgentOperation): Promise<AgentResponse> {
    // Implement comprehensive design validation
    return {
      success: false,
      errors: ['VALIDATE_DESIGN not yet implemented']
    };
  }

  // Storybook Integration Operations

  private async showStory(operation: AgentOperation): Promise<AgentResponse> {
    const { parameters } = operation;
    const query = parameters.query || '';
    
    if (!query) {
      return {
        success: false,
        errors: ['No component specified'],
        suggestions: [
          'Try: "Show me the Button component"',
          'Or: "Navigate to the Card story"'
        ]
      };
    }

    const story = this.mockStories[query.toLowerCase()];
    
    if (story) {
      return {
        success: true,
        data: { story },
        storybookCommand: {
          type: 'navigate',
          storyId: story.id,
          storyTitle: story.title
        },
        suggestions: [
          `✨ Navigating to ${story.title}`
        ],
        actionButtons: [
          {
            label: 'Analyze Props',
            action: 'analyze',
            parameters: { focus: 'props', component: story.component },
            variant: 'primary'
          },
          {
            label: 'Create Variant',
            action: 'create',
            parameters: { type: 'variant', component: story.component },
            variant: 'secondary'
          },
          {
            label: 'View Colors',
            action: 'analyze',
            parameters: { focus: 'visual', component: story.component },
            variant: 'outline'
          }
        ]
      };
    } else {
      const availableComponents = Object.keys(this.mockStories).map(k => k.charAt(0).toUpperCase() + k.slice(1));
      return {
        success: false,
        errors: [`Component "${query}" not found`],
        suggestions: [
          `Available components: ${availableComponents.join(', ')}`,
          'Try: "Show me the Button component"'
        ]
      };
    }
  }

  private async analyzeStory(operation: AgentOperation): Promise<AgentResponse> {
    const { parameters } = operation;
    const message = parameters.message || parameters.userMessage || '';
    const focus = parameters.analysisType || parameters.focus || 'general';
    
    console.log('analyzeStory - message:', message);
    console.log('analyzeStory - focus:', focus);
    console.log('analyzeStory - parameters:', parameters);

    // Use LLM-powered analysis for comprehensive understanding
    try {
      const { LLMAnalyzer } = await import('./agent/llm-analyzer');
      const analyzer = new LLMAnalyzer(parameters.projectPath || process.cwd());

      const analysisRequest = {
        message,
        focus: focus as any,
        component: parameters.componentName || this.extractComponentFromMessage(message),
        currentStory: parameters.currentStory,
        parameters
      };

      const result = await analyzer.analyzeStory(analysisRequest);

      if (result.success) {
        return {
          success: true,
          data: {
            analysis: result.analysis,
            visualBreakdown: result.visualBreakdown,
            metadata: result.metadata
          },
          suggestions: result.suggestions,
          actionButtons: result.actionButtons as ActionButton[]
        };
      } else {
        // Fallback to original logic if LLM analysis fails
        console.warn('LLM analysis failed, falling back to simple analysis');
      }
    } catch (error) {
      console.warn('Failed to load LLM analyzer:', error);
      // Continue with fallback
    }
    
    // Fallback to simple mock analysis
    return this.fallbackAnalysis(message, focus, parameters);
  }

  private formatAnalysisResponse(result: any): string {
    const lines = [];
    
    lines.push(`✅ **${result.analysis.summary}**`);
    lines.push('');
    
    if (result.analysis.designRationale) {
      lines.push('🎨 **Design Rationale:**');
      lines.push(result.analysis.designRationale);
      lines.push('');
    }

    if (result.analysis.tokenUsage.length > 0) {
      lines.push('🎯 **Design Tokens Used:**');
      result.analysis.tokenUsage.forEach((token: string) => {
        lines.push(`• ${token}`);
      });
      lines.push('');
    }

    if (result.analysis.accessibility) {
      lines.push('♿ **Accessibility:**');
      lines.push(result.analysis.accessibility);
      lines.push('');
    }

    if (result.analysis.improvements.length > 0) {
      lines.push('💡 **Improvement Suggestions:**');
      result.analysis.improvements.forEach((improvement: string) => {
        lines.push(`• ${improvement}`);
      });
      lines.push('');
    }

    if (result.metadata) {
      lines.push(`⚡ *Analysis completed in ${result.metadata.processingTime}ms with ${(result.metadata.confidence * 100).toFixed(0)}% confidence*`);
    }

    return lines.join('\\n');
  }

  private extractComponentFromMessage(message: string): string | undefined {
    const componentNames = ['button', 'card', 'input', 'modal', 'form', 'table', 'header', 'footer', 'nav', 'menu'];
    const lowerMessage = message.toLowerCase();
    
    for (const component of componentNames) {
      if (lowerMessage.includes(component)) {
        return component;
      }
    }
    
    return undefined;
  }

  private fallbackAnalysis(message: string, focus: string, parameters: any): AgentResponse {
    // Mock current story analysis - in real implementation, this would use Storybook's API
    const currentStoryAnalysis = {
      component: 'Button',
      story: 'Primary',
      props: {
        children: { type: 'string', defaultValue: 'Button' },
        variant: { type: 'string', options: ['primary', 'secondary', 'outline'], defaultValue: 'primary' },
        size: { type: 'string', options: ['small', 'medium', 'large'], defaultValue: 'medium' },
        disabled: { type: 'boolean', defaultValue: false }
      },
      designTokens: ['$colors.primary.500', '$spacing.md', '$typography.button'],
      visual: {
        backgroundColor: '#0ea5e9', // primary blue
        textColor: '#ffffff',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '16px'
      },
      accessibility: {
        hasAriaLabel: true,
        keyboardNavigable: true,
        contrastRatio: '4.5:1'
      }
    };

    // Handle specific visual questions
    if (focus === 'visual' || message.toLowerCase().includes('color')) {
      if (message.toLowerCase().includes('why') && message.toLowerCase().includes('blue')) {
        return {
          success: true,
          data: currentStoryAnalysis,
          suggestions: [
            `🎨 **Blue Button Explanation**`,
            `Primary variant uses brand blue (#0ea5e9) for maximum visibility.`,
            `This follows semantic color tokens for consistent design.`
          ],
          actionButtons: [
            {
              label: 'View Secondary Button',
              action: 'navigate',
              parameters: { component: 'button', variant: 'secondary' },
              variant: 'primary'
            },
            {
              label: 'View All Button Variants',
              action: 'search',
              parameters: { query: 'button', category: 'variants' },
              variant: 'outline'
            }
          ]
        };
      } else if (message.toLowerCase().includes('what color')) {
        return {
          success: true,
          data: currentStoryAnalysis,
          suggestions: [
            `🎨 **Current Colors**`,
            `Background: ${currentStoryAnalysis.visual.backgroundColor} (Primary Blue)`,
            `Text: ${currentStoryAnalysis.visual.textColor} (White)`
          ],
          actionButtons: [
            {
              label: 'View Secondary Button',
              action: 'navigate', 
              parameters: { component: 'button', variant: 'secondary' },
              variant: 'primary'
            },
            {
              label: 'Analyze Button Props',
              action: 'analyze',
              parameters: { focus: 'props', component: 'button' },
              variant: 'secondary'
            }
          ]
        };
      }
    }

    // Default story analysis - also cleaned up and with action buttons
    return {
      success: true,
      data: currentStoryAnalysis,
      suggestions: [
        `📋 **Current Story:** ${currentStoryAnalysis.component}/${currentStoryAnalysis.story}`,
        `Background: ${currentStoryAnalysis.visual.backgroundColor}`,
        `Text: ${currentStoryAnalysis.visual.textColor}`
      ],
      actionButtons: [
        {
          label: 'View Colors',
          action: 'analyze',
          parameters: { focus: 'visual', component: currentStoryAnalysis.component.toLowerCase() },
          variant: 'primary'
        },
        {
          label: 'Show Props',
          action: 'analyze',
          parameters: { focus: 'props', component: currentStoryAnalysis.component.toLowerCase() },
          variant: 'secondary'
        },
        {
          label: 'Navigate to Secondary',
          action: 'navigate',
          parameters: { component: currentStoryAnalysis.component.toLowerCase(), variant: 'secondary' },
          variant: 'outline'
        }
      ]
    };
  }

  private async handleGeneral(operation: AgentOperation): Promise<AgentResponse> {
    const { parameters } = operation;
    const message = parameters.message || '';
    
    // Provide helpful general responses and suggestions
    return {
      success: true,
      data: { message },
      suggestions: [
        '💬 I can help with:',
        '',
        '🎨 **Storybook:** "Show me the Button component" or "What color is this button?"',
        '🔧 **Components:** "Create a new button variant" or "Add a large size variant"',
        '🔍 **Search:** "Find form components" or "List all available components"',
        '📊 **Analysis:** "Analyze the current story" or "What props does this have?"',
        '⬆️ **Migration:** "Plan migration to v2.0" or "Execute the migration"',
        '',
        'What would you like to work on?'
      ]
    };
  }

  private async searchComponents(operation: AgentOperation): Promise<AgentResponse> {
    const { parameters } = operation;
    const query = parameters.query || '';

    // Mock component search - in real implementation, this would search Storybook's stories
    const allComponents = [
      { name: 'Button', variants: ['Primary', 'Secondary', 'Outline'], category: 'Form' },
      { name: 'Card', variants: ['Default', 'Elevated', 'Outlined'], category: 'Layout' },
      { name: 'Input', variants: ['Default', 'Error', 'Disabled'], category: 'Form' },
      { name: 'Modal', variants: ['Default', 'Large', 'Fullscreen'], category: 'Overlay' },
      { name: 'Navigation', variants: ['Horizontal', 'Vertical', 'Mobile'], category: 'Navigation' }
    ];

    let filteredComponents = allComponents;
    
    if (query) {
      filteredComponents = allComponents.filter(comp => 
        comp.name.toLowerCase().includes(query.toLowerCase()) ||
        comp.category.toLowerCase().includes(query.toLowerCase())
      );
    }

    if (filteredComponents.length === 0) {
      return {
        success: false,
        errors: [`No components found matching "${query}"`],
        suggestions: [
          'Available categories: Form, Layout, Overlay, Navigation',
          'Try: "Find form components" or "Show me all components"'
        ]
      };
    }

    const componentList = filteredComponents.map(comp => 
      `**${comp.name}** (${comp.category}) - ${comp.variants.length} variants`
    ).join('<br>');

    // If only one component found, offer to navigate to it
    let storybookCommand: StorybookCommand | undefined = undefined;
    if (filteredComponents.length === 1) {
      const component = filteredComponents[0];
      const componentKey = component.name.toLowerCase();
      const storyData = this.mockStories[componentKey];
      
      if (storyData) {
        storybookCommand = {
          type: 'navigate',
          storyId: storyData.id,
          storyTitle: storyData.title
        };
      }
    }

    return {
      success: true,
      data: { components: filteredComponents, query },
      storybookCommand,
      suggestions: [
        `🔍 **Found ${filteredComponents.length} component(s):**`,
        '',
        componentList,
        '',
        filteredComponents.length === 1 ? 
          `✨ Navigating to ${filteredComponents[0].name} component...` : 
          'Try: "Show me the Button component" to navigate to a specific story'
      ]
    };
  }

  // Governance and reuse-first tools
  async enforceReuseFirst(requestedComponent: string): Promise<AgentResponse> {
    const similarSections = this.findSimilarSections(requestedComponent);
    
    if (similarSections.length > 0) {
      return {
        success: false,
        errors: [`Similar sections found: ${similarSections.join(', ')}`],
        suggestions: [
          'Consider adding a variant to an existing section',
          'Review design tokens for consistency',
          'Use composition patterns when appropriate'
        ]
      };
    }

    return { success: true };
  }

  async analyzeDesignPatterns(requestedComponent: string): Promise<{
    existingPatterns: string[];
    tokenOpportunities: string[];
    compositionSuggestions: string[];
  }> {
    const patterns = this.context.currentProject.sections.map(section => ({
      name: section.name,
      category: section.category || 'component',
      variants: section.variants?.length || 0,
      tokens: Object.keys(section.tokens || {})
    }));

    // Find existing patterns that could be extended
    const existingPatterns = patterns
      .filter(pattern => this.calculateComponentSimilarity(requestedComponent, pattern.name) > 0.6)
      .map(pattern => `${pattern.name} (${pattern.variants} variants)`);

    // Identify token reuse opportunities
    const allTokens = new Set<string>();
    patterns.forEach(pattern => pattern.tokens.forEach(token => allTokens.add(token)));
    const tokenOpportunities = Array.from(allTokens).slice(0, 5); // Top 5 most used tokens

    // Suggest composition patterns
    const compositionSuggestions = this.generateCompositionSuggestions(requestedComponent, patterns);

    return {
      existingPatterns,
      tokenOpportunities,
      compositionSuggestions
    };
  }

  async validateAgainstGovernance(operation: AgentOperation): Promise<{
    canProceed: boolean;
    governance: any; // Will be GovernanceReport when governance is integrated
    recommendations: string[];
  }> {
    // Placeholder for governance integration
    // This will connect to GovernanceEngine once implemented
    
    const recommendations = [
      'Follow established naming conventions',
      'Reuse existing design tokens',
      'Ensure accessibility compliance',
      'Add comprehensive Storybook stories'
    ];

    // Simple validation based on operation type
    const canProceed = operation.type === 'ADD_VARIANT' || 
                      (operation.type === 'CREATE_SECTION' && 
                       !this.findSimilarSections(operation.target).length);

    return {
      canProceed,
      governance: { passed: canProceed, rules: [] },
      recommendations
    };
  }

  private findSimilarSections(componentName: string): string[] {
    return this.context.currentProject.sections
      .filter(section => 
        this.calculateComponentSimilarity(componentName, section.name) > 0.7
      )
      .map(section => section.name);
  }

  private calculateComponentSimilarity(name1: string, name2: string): number {
    // Enhanced similarity calculation for component names
    const n1 = name1.toLowerCase().replace(/[^a-z]/g, '');
    const n2 = name2.toLowerCase().replace(/[^a-z]/g, '');
    
    // Check for exact matches in stems
    const stem1 = this.extractComponentStem(n1);
    const stem2 = this.extractComponentStem(n2);
    
    if (stem1 === stem2) return 1.0;
    
    // Check for partial matches
    if (n1.includes(n2) || n2.includes(n1)) return 0.8;
    
    // Levenshtein distance for remaining cases
    return this.calculateLevenshteinSimilarity(n1, n2);
  }

  private extractComponentStem(componentName: string): string {
    // Extract the core component type (button, card, input, etc.)
    const componentMap: Record<string, string> = {
      'btn': 'button',
      'button': 'button',
      'card': 'card',
      'input': 'input',
      'txt': 'input',
      'text': 'input',
      'modal': 'modal',
      'form': 'form',
      'list': 'list',
      'item': 'item'
    };
    
    const lowerName = componentName.toLowerCase();
    
    // Check direct mappings first (handles abbreviations)
    for (const [key, value] of Object.entries(componentMap)) {
      if (lowerName.includes(key)) return value;
    }
    
    return lowerName;
  }

  private calculateLevenshteinSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  private generateCompositionSuggestions(requestedComponent: string, patterns: any[]): string[] {
    const suggestions = [];
    
    // Suggest combining existing elements
    if (patterns.filter(p => p.category === 'element').length >= 2) {
      suggestions.push('Combine existing elements instead of creating a new component');
    }
    
    // Suggest layout patterns
    if (requestedComponent.toLowerCase().includes('list') || 
        requestedComponent.toLowerCase().includes('grid')) {
      suggestions.push('Use layout sections with existing components');
    }
    
    // Suggest variant approach
    const similarPatterns = patterns.filter(p => 
      this.calculateComponentSimilarity(requestedComponent, p.name) > 0.5
    );
    
    if (similarPatterns.length > 0) {
      suggestions.push(`Consider adding variants to: ${similarPatterns[0].name}`);
    }
    
    return suggestions;
  }
  
  // Migration operations
  private async planMigration(operation: AgentOperation): Promise<AgentResponse> {
    const { parameters } = operation;
    const { fromVersion, toVersion, projectPath } = parameters;

    try {
      // Import migration engine dynamically to avoid build dependencies
      const migrationModule = await import('./migration/migration-engine');
      
      const engine = new migrationModule.DezzyMigrationEngine(projectPath || process.cwd());
      const plan = await engine.planMigration(fromVersion, toVersion);
      
      const migrationPlan = {
        steps: plan.migrations.map(m => ({
          id: m.id,
          description: m.description,
          breaking: m.breaking || false
        })),
        fromVersion: plan.fromVersion,
        toVersion: plan.toVersion,
        estimatedTime: plan.estimatedTime,
        breakingChanges: plan.breakingChanges || false
      };

      return {
        success: true,
        data: plan,
        migrationPlan,
        suggestions: [
          `📋 Migration plan ready: ${migrationPlan.steps.length} step(s)`,
          `⏱️  Estimated time: ${Math.round(migrationPlan.estimatedTime / 1000)} seconds`,
          migrationPlan.breakingChanges ? '⚠️  Contains breaking changes' : '✅ No breaking changes',
          'Use "execute migration" to proceed or ask questions about the changes'
        ]
      };
    } catch (error) {
      return {
        success: false,
        errors: [error instanceof Error ? error.message : 'Failed to plan migration'],
        suggestions: [
          'Check that your project is a valid Dezzy project',
          'Ensure target version is available',
          'Try running: dezzy migrate --check'
        ]
      };
    }
  }

  private async executeMigration(operation: AgentOperation): Promise<AgentResponse> {
    const { parameters } = operation;
    const { migrationPlan, projectPath, dryRun = false } = parameters;

    try {
      const migrationModule = await import('./migration/migration-engine');
      
      const engine = new migrationModule.DezzyMigrationEngine(projectPath || process.cwd());
      
      // Execute the migration with progress tracking
      const result = await engine.executeMigration(migrationPlan, projectPath, {
        dryRun,
        verbose: true,
        skipBackup: false
      });

      const migrationProgress = {
        currentStep: result.completedMigrations?.length || 0,
        totalSteps: migrationPlan?.migrations?.length || 0,
        status: result.success ? 'completed' as const : 'failed' as const,
        currentStepDescription: result.success ? 'Migration completed' : 'Migration failed'
      };

      return {
        success: result.success,
        data: result,
        migrationProgress,
        suggestions: result.success ? [
          '✅ Migration completed successfully!',
          `📁 Backup created: ${result.backup?.id || 'N/A'}`,
          `📝 Files changed: ${result.changes?.length || 0}`,
          'Run your build and tests to ensure everything works'
        ] : [
          '❌ Migration failed',
          `💾 Rollback available: ${result.rollbackAvailable ? 'Yes' : 'No'}`,
          'Check the error details above',
          'Use "rollback migration" to revert changes if needed'
        ],
        errors: result.success ? undefined : [
          result.error || 'Migration execution failed',
          ...(result.failedMigration ? [`Failed at: ${result.failedMigration.id}`] : [])
        ]
      };
    } catch (error) {
      return {
        success: false,
        errors: [error instanceof Error ? error.message : 'Migration execution failed'],
        suggestions: [
          'Check migration logs for details',
          'Ensure project is in a clean state',
          'Try rolling back to previous version'
        ]
      };
    }
  }

  private async rollbackMigration(operation: AgentOperation): Promise<AgentResponse> {
    const { parameters } = operation;
    const { projectPath, backupId } = parameters;

    try {
      const migrationModule = await import('./migration/migration-engine');
      
      const engine = new migrationModule.DezzyMigrationEngine(projectPath || process.cwd());
      const success = await engine.rollback(projectPath, backupId);

      return {
        success,
        suggestions: success ? [
          '✅ Successfully rolled back to previous state',
          'Your project has been restored',
          'Verify that everything is working correctly'
        ] : [
          '❌ Rollback failed',
          'Check backup availability',
          'Manual recovery may be needed'
        ],
        errors: success ? undefined : ['Rollback operation failed']
      };
    } catch (error) {
      return {
        success: false,
        errors: [error instanceof Error ? error.message : 'Rollback failed'],
        suggestions: [
          'Check that backup exists and is valid',
          'Ensure project directory is writable',
          'Try manual file restoration if needed'
        ]
      };
    }
  }
  
  // Enhanced agent helper methods
  
  /**
   * Extract user intent from natural language message using LLM classification
   */
  private async extractUserIntent(message: string): Promise<UserIntent> {
    try {
      const classification = await this.intentClassifier.classify(message);
      
      return {
        primary: classification.intent,
        confidence: classification.confidence,
        parameters: {
          message,
          ...classification.extractedEntities,
          reasoning: classification.reasoning,
          classifierType: classification.classifierType,
          modelUsed: classification.modelUsed
        }
      };
    } catch (error) {
      // Fallback to simple intent detection if LLM fails
      console.warn('LLM intent classification failed, using fallback:', error);
      return this.fallbackIntentExtraction(message);
    }
  }
  
  /**
   * Fallback intent extraction for when LLM classification fails
   */
  private fallbackIntentExtraction(message: string): UserIntent {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('show') || lowerMessage.includes('navigate')) {
      return {
        primary: 'SHOW_STORY',
        confidence: 0.6,
        parameters: { message, action: 'navigate', query: this.extractComponentName(message) }
      };
    }
    
    if (lowerMessage.includes('what') || lowerMessage.includes('color')) {
      return {
        primary: 'ANALYZE_STORY',
        confidence: 0.6,
        parameters: { message, action: 'analyze', focus: 'visual' }
      };
    }
    
    return {
      primary: 'GENERAL',
      confidence: 0.3,
      parameters: { message, query: message }
    };
  }
  
  /**
   * Extract component/story name from user message
   */
  private extractComponentName(message: string): string {
    const lowerMessage = message.toLowerCase();
    
    // Common component patterns
    const componentPatterns = [
      /button/g, /card/g, /input/g, /form/g, /modal/g, /nav/g, /header/g, /footer/g,
      /list/g, /item/g, /menu/g, /tab/g, /panel/g, /section/g, /grid/g, /layout/g
    ];
    
    for (const pattern of componentPatterns) {
      if (pattern.test(lowerMessage)) {
        const match = lowerMessage.match(pattern);
        return match ? match[0] : '';
      }
    }
    
    // Extract words after "show", "navigate to", etc.
    const showMatch = lowerMessage.match(/(?:show|navigate to|go to)\s+(?:the\s+)?(\w+)/);
    if (showMatch) {
      return showMatch[1];
    }
    
    return '';
  }
  
  /**
   * Convert user intent to operation
   */
  private intentToOperation(intent: UserIntent, context?: OperationContext): AgentOperation {
    const operationType = intent.primary as AgentOperation['type'];
    
    // Map extracted entities to proper parameters
    const parameters: Record<string, any> = {
      ...intent.parameters,
      userMessage: intent.parameters.message,
      confidence: intent.confidence,
      projectPath: context?.projectPath || this.context.currentProject.projectPath
    };
    
    // Ensure focus is properly set for analysis operations
    if (operationType === 'ANALYZE_STORY') {
      parameters.focus = intent.parameters.analysisType || intent.parameters.focus || 'general';
    }
    
    return {
      type: operationType,
      target: intent.parameters.target || intent.parameters.component || 'general',
      parameters,
      context: this.context
    };
  }
  
  /**
   * Format agent response for conversation
   */
  private formatConversationResponse(response: AgentResponse, intent: UserIntent): string {
    let formattedResponse = '';
    
    if (response.success) {
      formattedResponse += '✅ ';
      
      // Add operation-specific success messages
      if (intent.primary === 'PLAN_MIGRATION' && response.migrationPlan) {
        formattedResponse += `Migration plan created successfully!\n\n`;
        formattedResponse += `📋 **Migration Details:**\n`;
        formattedResponse += `• From: ${response.migrationPlan.fromVersion} → ${response.migrationPlan.toVersion}\n`;
        formattedResponse += `• Steps: ${response.migrationPlan.steps.length}\n`;
        formattedResponse += `• Estimated time: ~${Math.round(response.migrationPlan.estimatedTime / 1000)} seconds\n`;
        
        if (response.migrationPlan.breakingChanges) {
          formattedResponse += `• ⚠️ **Contains breaking changes**\n`;
        }
      } else {
        formattedResponse += 'Operation completed successfully!\n';
      }
    } else {
      formattedResponse += '❌ ';
      if (response.errors && response.errors.length > 0) {
        formattedResponse += `Operation failed: ${response.errors[0]}\n`;
      } else {
        formattedResponse += 'Operation failed for an unknown reason.\n';
      }
    }
    
    // Add suggestions if available
    if (response.suggestions && response.suggestions.length > 0) {
      formattedResponse += '\n💡 **Suggestions:**\n';
      response.suggestions.forEach(suggestion => {
        formattedResponse += `• ${suggestion}\n`;
      });
    }
    
    // Add errors if any
    if (response.errors && response.errors.length > 1) {
      formattedResponse += '\n⚠️ **Issues:**\n';
      response.errors.slice(1).forEach(error => {
        formattedResponse += `• ${error}\n`;
      });
    }
    
    return formattedResponse.trim();
  }
  
  /**
   * Record conversation turn for context and learning
   */
  private getRecentMessages(): string[] {
    return this.conversationHistory
      .slice(-5)
      .map(turn => turn.userMessage);
  }

  private recordConversationTurn(
    id: string,
    userMessage: string,
    agentResponse: string,
    metadata: {
      operationType?: string;
      success: boolean;
      executionTime: number;
      traceId?: string;
      error?: string;
    }
  ): void {
    const turn: ConversationTurn = {
      id,
      userMessage,
      agentResponse,
      timestamp: new Date(),
      operationType: metadata.operationType,
      success: metadata.success,
      traceId: metadata.traceId,
      error: metadata.error
    };
    
    this.conversationHistory.push(turn);
    
    // Keep only recent conversation history (last 20 turns)
    if (this.conversationHistory.length > 20) {
      this.conversationHistory = this.conversationHistory.slice(-20);
    }
  }
  
  /**
   * Get conversation context for enhanced prompts
   */
  public getConversationContext(limit: number = 5): ConversationTurn[] {
    return this.conversationHistory.slice(-limit);
  }
  
  /**
   * Estimate operation complexity for routing decisions
   */
  public estimateComplexity(operation: AgentOperation): 'low' | 'medium' | 'high' {
    const highComplexity = ['CREATE_SECTION', 'EXECUTE_MIGRATION'];
    const mediumComplexity = ['ADD_VARIANT', 'UPDATE_VIEW', 'PLAN_MIGRATION'];
    
    if (highComplexity.includes(operation.type)) return 'high';
    if (mediumComplexity.includes(operation.type)) return 'medium';
    return 'low';
  }
  
  /**
   * Generate unique turn ID
   */
  private generateTurnId(): string {
    return `turn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Handle memory alerts from memory monitor
   */
  private handleMemoryAlert(alert: any): void {
    // Force cleanup on memory warnings
    if (alert.level === 'warning') {
      console.log('🧹 Memory warning triggered cleanup');
      this.tracer.forceCleanup().catch(console.error);
    } else if (alert.level === 'critical') {
      console.log('🚨 Critical memory - forcing aggressive cleanup');
      this.tracer.forceCleanup().catch(console.error);
      
      // Try garbage collection if available
      this.memoryMonitor.forceGarbageCollection();
      
      // Trim conversation history more aggressively
      if (this.conversationHistory.length > 10) {
        this.conversationHistory = this.conversationHistory.slice(-10);
      }
    }
  }

  /**
   * Get tracing and debugging information including memory stats
   */
  getTracingInfo(): any {
    const memoryStatus = this.memoryMonitor.getStatus();
    
    return {
      debugInfo: this.tracer.getDebugInfo(),
      conversationHistory: this.conversationHistory.slice(-5), // Recent conversations
      performanceMetrics: {
        averageResponseTime: this.calculateAverageResponseTime(),
        successRate: this.calculateSuccessRate(),
        mostCommonIntents: this.getMostCommonIntents()
      },
      memory: {
        current: {
          heapUsedMB: Math.round(memoryStatus.current.heapUsed / 1024 / 1024),
          heapTotalMB: Math.round(memoryStatus.current.heapTotal / 1024 / 1024),
          rssMB: Math.round(memoryStatus.current.rss / 1024 / 1024)
        },
        thresholds: memoryStatus.thresholds,
        trend: memoryStatus.trend,
        recentAlerts: memoryStatus.alerts.slice(-3).map(alert => ({
          level: alert.level,
          message: alert.message,
          timestamp: alert.timestamp
        }))
      }
    };
  }
  
  /**
   * Analyze traces and extract insights
   */
  async analyzePerformance(timeRange?: { start: Date; end: Date }) {
    return await this.tracer.analyzeTraces(timeRange);
  }
  
  /**
   * Update trace with user feedback (for learning)
   */
  updateUserFeedback(traceId: string, satisfaction: 'positive' | 'negative' | 'neutral', followUpAction?: string) {
    this.tracer.updateTraceOutcome(traceId, {
      userSatisfaction: satisfaction,
      followUpAction: followUpAction as any
    });
  }
  
  /**
   * Get detailed information about a specific trace (for right-click debugging)
   */
  async getTraceDetails(traceId: string) {
    return await this.tracer.getTraceDetails(traceId);
  }
  
  // Performance calculation helpers
  private calculateAverageResponseTime(): number {
    const recentTurns = this.conversationHistory.slice(-10);
    if (recentTurns.length === 0) return 0;
    
    // This would need execution time tracking in the conversation turns
    // For now, return a placeholder
    return 500; // milliseconds
  }
  
  private calculateSuccessRate(): number {
    const recentTurns = this.conversationHistory.slice(-10);
    if (recentTurns.length === 0) return 1;
    
    const successfulTurns = recentTurns.filter(turn => turn.success);
    return successfulTurns.length / recentTurns.length;
  }
  
  private getMostCommonIntents(): { intent: string; count: number }[] {
    const intentCounts = new Map<string, number>();
    
    this.conversationHistory.slice(-20).forEach(turn => {
      if (turn.operationType) {
        intentCounts.set(turn.operationType, (intentCounts.get(turn.operationType) || 0) + 1);
      }
    });
    
    return Array.from(intentCounts.entries())
      .map(([intent, count]) => ({ intent, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }
}

export function createAgentContext(
  projectData: AgentContext['currentProject']
): AgentContext {
  const sectionRegistry = projectData.sections.reduce((acc, section) => {
    acc[section.id] = section;
    return acc;
  }, {} as import('./types').SectionManifestRegistry);

  return {
    currentProject: projectData,
    validator: new DezzyValidator(viewSchema, sectionRegistry),
    compiler: new DezzyCompiler()
  };
}